import { cn } from "../../../utils/cn.js";
import { cva } from "class-variance-authority";
import { CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/AlertBanner/AlertBanner.tsx
const typeIconMap = {
	danger: /* @__PURE__ */ jsx(WarningCircleIcon, {
		size: 16,
		weight: "fill"
	}),
	warning: /* @__PURE__ */ jsx(WarningIcon, {
		size: 16,
		weight: "fill"
	}),
	success: /* @__PURE__ */ jsx(CheckCircleIcon, {
		size: 16,
		weight: "fill"
	}),
	information: /* @__PURE__ */ jsx(InfoIcon, {
		size: 16,
		weight: "fill"
	})
};
const subtleIconColorMap = {
	danger: "text-icon-danger",
	warning: "text-icon-warning",
	success: "text-icon-success",
	information: "text-icon-information"
};
const alertBannerVariants = cva(["flex items-start gap-md p-xs my-xxxs rounded-xs w-full"], {
	variants: {
		type: {
			danger: "",
			warning: "",
			success: "",
			information: ""
		},
		severity: {
			subtle: "",
			bold: ""
		}
	},
	compoundVariants: [
		{
			type: "danger",
			severity: "subtle",
			className: "bg-background-system-danger"
		},
		{
			type: "danger",
			severity: "bold",
			className: "bg-background-system-danger-bold"
		},
		{
			type: "warning",
			severity: "subtle",
			className: "bg-background-system-warning"
		},
		{
			type: "warning",
			severity: "bold",
			className: "bg-background-system-warning-bold"
		},
		{
			type: "success",
			severity: "subtle",
			className: "bg-background-system-success"
		},
		{
			type: "success",
			severity: "bold",
			className: "bg-background-system-success-bold"
		},
		{
			type: "information",
			severity: "subtle",
			className: "bg-background-system-information"
		},
		{
			type: "information",
			severity: "bold",
			className: "bg-background-system-information-bold"
		}
	],
	defaultVariants: { severity: "subtle" }
});
/**
* Page-level feedback banner for blocking errors and non-blocking guidance.
*
* Use `type="danger"` for blocking errors (pair with inline field validation)
* and `type="warning"` for non-blocking guidance. The `severity` prop switches
* between a light tinted background (subtle) and a saturated background (bold).
*
* @param props - AlertBanner configuration
* @returns The rendered banner element
*/
function AlertBanner({ type, severity = "subtle", title, children, icon = true, onDismiss, action, className }) {
	const isBold = severity === "bold";
	const textColor = isBold ? "text-text-primary-inverse" : "text-text-primary";
	const iconColor = isBold ? "text-icon-inverse" : subtleIconColorMap[type];
	const dismissColor = isBold ? "text-icon-inverse" : "text-icon-primary";
	return /* @__PURE__ */ jsxs("div", {
		role: "alert",
		className: cn(alertBannerVariants({
			type,
			severity
		}), className),
		children: [
			icon && /* @__PURE__ */ jsx("span", {
				className: cn("mt-[1px] shrink-0", iconColor),
				children: icon === true ? typeIconMap[type] : icon
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "flex min-w-0 flex-1 flex-wrap items-start gap-xs",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "flex flex-[1_1_240px] flex-col gap-xxxs min-w-0",
					children: [title && /* @__PURE__ */ jsx("p", {
						className: cn("text-body-base leading-body-base font-heading-semi-bold", textColor),
						children: title
					}), children && /* @__PURE__ */ jsx("div", {
						className: cn("text-body-base leading-body-base font-body font-heading-medium", textColor),
						children
					})]
				}), action && /* @__PURE__ */ jsx("button", {
					className: cn("shrink-0 self-start cursor-pointer underline rounded-xs", "text-body-base leading-body-base font-heading-medium", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", textColor),
					onClick: action.onClick,
					type: "button",
					children: action.label
				})]
			}),
			onDismiss && /* @__PURE__ */ jsx("button", {
				"aria-label": "Dismiss",
				className: cn("mt-[1px] shrink-0 cursor-pointer", dismissColor),
				onClick: onDismiss,
				type: "button",
				children: /* @__PURE__ */ jsx(XIcon, { size: 16 })
			})
		]
	});
}
//#endregion
export { AlertBanner };

//# sourceMappingURL=AlertBanner.js.map